import { debounce } from 'lodash';
import { type SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Debounced callback that is cancelled on unmount, so a pending setState never fires afterwards.
 * The callback lives in a ref, so an inline arrow does not rebuild the debouncer on every render.
 */
export function useDebounceCallback<Args extends Array<unknown>>(
  func: (...args: Args) => void,
  delay = 500,
) {
  const funcRef = useRef(func);
  funcRef.current = func;

  const debounced = useMemo(
    () => debounce((...args: Args) => funcRef.current(...args), delay),
    [delay],
  );

  useEffect(() => () => debounced.cancel(), [debounced]);

  return debounced;
}

export function useDebounceValue<T>(value: T, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const previousRef = useRef(value);
  const update = useDebounceCallback<[SetStateAction<T>]>(setDebouncedValue, delay);

  // Schedules the update during render, not in an effect, to keep the timing call sites rely on.
  if (previousRef.current !== value) {
    update(value);
    previousRef.current = value;
  }

  return [debouncedValue, update] as const;
}
